// QuestSystem.ts — framework-free quest tracking engine

import type { EventBus } from './EventBus.js'

export interface QuestDefinition {
  id: string
  title: string
  description: string
  type: 'daily' | 'weekly' | 'achievement'
  target: number
  metric: 'towers_placed' | 'units_killed' | 'units_sent' | 'matches_won' | 'gold_earned' | 'spells_cast'
  trackEvent: 'TOWER_PLACED' | 'UNIT_KILLED' | 'UNIT_SENT' | 'MATCH_END' | 'GOLD_CHANGED' | 'SPELL_CAST'
  reward: { xp: number; gold: number; packMeterIncrease?: number }
  expiresAt?: number // unix ms, optional for achievements
}

export interface QuestState {
  definitionId: string
  progress: number
  completed: boolean
  claimedAt?: number
  resetDate?: string // ISO date string — tracks last daily reset
}

export class QuestSystem {
  private definitions: Map<string, QuestDefinition> = new Map()
  private states: Map<string, QuestState> = new Map()
  private unsubscribes: Array<() => void> = []

  /**
   * Construct a QuestSystem.
   *
   * @param busOrDefinitions - Either an EventBus to subscribe to immediately, or
   *   a QuestDefinition[] when constructing without a bus (call subscribeToEventBus later).
   * @param definitions - Quest definitions when busOrDefinitions is an EventBus.
   */
  constructor(busOrDefinitions: EventBus | QuestDefinition[], definitions: QuestDefinition[] = []) {
    let bus: EventBus | null = null
    let defs: QuestDefinition[]

    if (Array.isArray(busOrDefinitions)) {
      // Called as new QuestSystem(definitions) — no bus yet
      defs = busOrDefinitions
      bus = null
    } else {
      // Called as new QuestSystem(bus, definitions)
      bus = busOrDefinitions
      defs = definitions
    }

    for (const def of defs) {
      this.definitions.set(def.id, def)
      if (!this.states.has(def.id)) {
        this.states.set(def.id, { definitionId: def.id, progress: 0, completed: false })
      }
    }

    if (bus !== null) {
      this._subscribeToBus(bus)
    }
  }

  /**
   * Re-subscribe to a new EventBus (called each time a new match starts so that
   * quest progress is tracked against the current match's event stream).
   * Existing bus subscriptions are unregistered first.
   */
  subscribeToEventBus(bus: EventBus): void {
    // Unsubscribe from any previous bus
    for (const unsub of this.unsubscribes) {
      unsub()
    }
    this.unsubscribes = []
    this._subscribeToBus(bus)
  }

  private _subscribeToBus(bus: EventBus): void {
    const defs = Array.from(this.definitions.values())

    // Group definitions by trackEvent and subscribe once per event type
    const byEvent = new Map<string, QuestDefinition[]>()
    for (const def of defs) {
      const group = byEvent.get(def.trackEvent) ?? []
      group.push(def)
      byEvent.set(def.trackEvent, group)
    }

    for (const [eventType, eventDefs] of byEvent) {
      const unsub = bus.on(eventType as QuestDefinition['trackEvent'], (event) => {
        for (const def of eventDefs) {
          this._handleEvent(def, event)
        }
      })
      this.unsubscribes.push(unsub)
    }
  }

  private _handleEvent(def: QuestDefinition, event: unknown): void {
    // For match-win quests only count actual wins, not losses or draws.
    if (def.metric === 'matches_won' && def.trackEvent === 'MATCH_END') {
      const payload = (event as { payload?: Record<string, unknown> })?.payload
      if (payload?.['outcome'] !== 'win') return
    }

    const state = this.states.get(def.id) ?? { definitionId: def.id, progress: 0, completed: false }
    if (state.completed) return
    state.progress = Math.min(state.progress + 1, def.target)
    if (state.progress >= def.target) {
      state.completed = true
    }
    this.states.set(def.id, state)
  }

  destroy(): void {
    for (const unsub of this.unsubscribes) {
      unsub()
    }
    this.unsubscribes = []
  }

  loadState(states: QuestState[]): void {
    for (const s of states) {
      this.states.set(s.definitionId, s)
    }
  }

  resetDailyQuests(todayISO: string): void {
    for (const [id, def] of this.definitions) {
      if (def.type !== 'daily') continue
      const state = this.states.get(id)
      if (state === undefined || state.resetDate === todayISO) continue
      this.states.set(id, {
        definitionId: id,
        progress: 0,
        completed: false,
        resetDate: todayISO,
      })
    }
  }

  recordMetric(metric: QuestDefinition['metric'], amount: number): string[] {
    const justCompleted: string[] = []
    for (const [id, def] of this.definitions) {
      if (def.metric !== metric) continue
      const state = this.states.get(id) ?? { definitionId: id, progress: 0, completed: false }
      if (state.completed) continue
      state.progress = Math.min(state.progress + amount, def.target)
      if (state.progress >= def.target) {
        state.completed = true
        justCompleted.push(id)
      }
      this.states.set(id, state)
    }
    return justCompleted
  }

  claimReward(questId: string): { xp: number; gold: number; packMeterIncrease: number } | null {
    const state = this.states.get(questId)
    const def = this.definitions.get(questId)
    if (!state || !def || !state.completed || state.claimedAt !== undefined) return null
    state.claimedAt = Date.now()
    this.states.set(questId, state)
    return {
      xp: def.reward.xp,
      gold: def.reward.gold,
      packMeterIncrease: def.reward.packMeterIncrease ?? 0,
    }
  }

  getState(questId: string): QuestState | undefined {
    return this.states.get(questId)
  }

  getAllStates(): QuestState[] {
    return Array.from(this.states.values())
  }

  getDefinition(questId: string): QuestDefinition | undefined {
    return this.definitions.get(questId)
  }

  getActiveDailyQuests(): Array<{ def: QuestDefinition; state: QuestState }> {
    const now = Date.now()
    const result: Array<{ def: QuestDefinition; state: QuestState }> = []
    for (const [id, def] of this.definitions) {
      if (def.type !== 'daily') continue
      if (def.expiresAt !== undefined && def.expiresAt < now) continue
      const state = this.states.get(id) ?? { definitionId: id, progress: 0, completed: false }
      result.push({ def, state })
    }
    return result
  }
}
